// src/components/SuperAdminUsers.jsx
import { useEffect, useMemo, useState, useContext } from "react";
import {
  Box,
  Paper,
  Typography,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  CircularProgress,
  Stack,
  Grid,
} from "@mui/material";
import { collection, doc, getDoc, getDocs, onSnapshot } from "firebase/firestore";
import { db } from "../firebase";
import { useAuth } from "../context/AuthContext";
import LeftMenu from "./menus/LeftMenu";
import HeaderBar from "./headers/HeaderBar";
import { LanguageContext } from "../context/LanguageContext";

const MONTHLY_PRICE_USD = 15;

export default function SuperAdminUsers() {
  const { currentUser } = useAuth();
  const { translate } = useContext(LanguageContext);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [checkingRole, setCheckingRole] = useState(true);
  const [profiles, setProfiles] = useState([]);
  const [loadingProfiles, setLoadingProfiles] = useState(false);
  const [tenantTokens, setTenantTokens] = useState({});
  const [loadingTokens, setLoadingTokens] = useState(false);
  const [tenantPlans, setTenantPlans] = useState({});

  // Watch current user's role
  useEffect(() => {
    if (!currentUser) {
      setIsSuperAdmin(false);
      setCheckingRole(false);
      return;
    }
    const unsub = onSnapshot(
      doc(db, "profilsUsers", currentUser.uid),
      (snap) => {
        if (snap.exists()) {
          setIsSuperAdmin(snap.data()?.role === "SuperAdmin");
        } else {
          setIsSuperAdmin(false);
        }
        setCheckingRole(false);
      }
    );
    return () => unsub();
  }, [currentUser]);

  // Fetch all user profiles
  useEffect(() => {
    if (!isSuperAdmin) return;
    setLoadingProfiles(true);
    getDocs(collection(db, "profilsUsers"))
      .then((snap) => {
        const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setProfiles(list);
      })
      .finally(() => setLoadingProfiles(false));
  }, [isSuperAdmin]);

  // Fetch token usage per tenant
  useEffect(() => {
    if (!isSuperAdmin || profiles.length === 0) {
      setTenantTokens({});
      return;
    }
    const uniqueTenantIds = Array.from(
      new Set(
        profiles
          .map((p) => p.tenantId)
          .filter((t) => typeof t === "string" && t.trim() !== "")
      )
    );
    if (uniqueTenantIds.length === 0) {
      setTenantTokens({});
      return;
    }

    setLoadingTokens(true);
    Promise.all(
      uniqueTenantIds.map(async (tenantId) => {
        try {
          // fetch tenant plan
          const tenantDocSnap = await getDoc(doc(db, "tenants", tenantId));
          const plan = tenantDocSnap.exists()
            ? tenantDocSnap.data()?.plan || "unknown"
            : "unknown";

          const agentsSnap = await getDocs(
            collection(db, "tenants", tenantId, "agents")
          );
          let total = 0;
          for (const agentDoc of agentsSnap.docs) {
            const usageSnap = await getDocs(
              collection(
                db,
                "tenants",
                tenantId,
                "agents",
                agentDoc.id,
                "usage_daily"
              )
            );
            usageSnap.forEach((d) => {
              total += Number(d.data()?.totalTokens || 0) || 0;
            });
          }
          return { tenantId, total, plan };
        } catch (e) {
          console.error("Failed to load tokens for tenant", tenantId, e);
          return { tenantId, total: 0, plan: "unknown" };
        }
      })
    )
      .then((arr) => {
        const mapTokens = {};
        const mapPlans = {};
        arr.forEach(({ tenantId, total, plan }) => {
          mapTokens[tenantId] = total;
          mapPlans[tenantId] = plan;
        });
        setTenantTokens(mapTokens);
        setTenantPlans(mapPlans);
      })
      .finally(() => setLoadingTokens(false));
  }, [isSuperAdmin, profiles]);

  const rows = useMemo(() => {
    return profiles.map((p) => ({
      id: p.id,
      name: p.name || "N/A",
      email: p.email || "N/A",
      role: p.role || "Normal User",
      // Prefer per-user plan (written by useIsPremium); fallback to tenant plan
      plan:
        p.plan ||
        (p.tenantId ? tenantPlans[p.tenantId] || "unknown" : "none"),
      tokens: p.tenantId ? tenantTokens[p.tenantId] || 0 : 0,
    }));
  }, [profiles, tenantTokens, tenantPlans]);

  const globalTotalTokens = useMemo(() => {
    const seen = new Set();
    let total = 0;
    Object.entries(tenantTokens).forEach(([tid, value]) => {
      if (seen.has(tid)) return;
      seen.add(tid);
      total += value || 0;
    });
    return total;
  }, [tenantTokens]);

  const counts = useMemo(() => {
    const premium = rows.filter((r) => r.plan === "premium").length;
    const free = rows.filter((r) => r.plan === "free" || r.plan === "none").length;
    const other = Math.max(rows.length - premium - free, 0);
    return { premium, free, other };
  }, [rows]);

  const monthlyRevenue = counts.premium * MONTHLY_PRICE_USD;

  if (checkingRole || !currentUser) {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "60vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!isSuperAdmin) {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "60vh",
          p: 3,
        }}
      >
        <Typography variant="h6">
          Access restricted to SuperAdmin users.
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        width: "100%",
        bgcolor: "background.default",
        color: "text.primary",
        minHeight: "100vh",
      }}
    >
      <LeftMenu />
      <Box sx={{ flex: 1, p: 3 }}>
        <HeaderBar
          title={translate("SuperAdminTitle") || "Super Admin"}
          subtitle={translate("SuperAdminSubtitle") || "All users, roles, and token consumption"}
        />
        <Stack spacing={2} sx={{ mt: 3 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={8}>
              <Paper
                variant="outlined"
                sx={{
                  p: 2,
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Typography fontWeight={700}>
                  {translate("TotalTokensAllTenants") || "Total tokens (all tenants)"}: {globalTotalTokens.toLocaleString()}
                </Typography>
                {(loadingProfiles || loadingTokens) && <CircularProgress size={20} aria-label="loading-tokens" />}
              </Paper>
            </Grid>
            <Grid item xs={12} md={4}>
              <Paper
                variant="outlined"
                sx={{
                  p: 2,
                  display: "flex",
                  flexDirection: "column",
                  gap: 1,
                  height: "100%",
                }}
              >
                <Typography fontWeight={700}>{translate("MonthlyRevenueEst") || "Monthly revenue (est.)"}</Typography>
                <Typography variant="h5" fontWeight={800}>
                  ${monthlyRevenue.toLocaleString()} / mo
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {(translate("MonthlyRevenueHint") || "Based on {count} premium accounts at $15/mo.")
                    .replace("{count}", counts.premium)}
                </Typography>
              </Paper>
            </Grid>
          </Grid>

          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <Paper
                variant="outlined"
                sx={{
                  p: 2,
                  display: "flex",
                  flexDirection: "column",
                  gap: 2,
                  alignItems: "center",
                }}
              >
                <Typography fontWeight={700}>{translate("UsersByPlan") || "Users by plan"}</Typography>
                <Box
                  sx={{
                    width: 180,
                    height: 180,
                    borderRadius: "50%",
                    background: `conic-gradient(#4f46e5 0deg ${counts.premium + counts.free + counts.other === 0 ? 0 : (counts.premium / (counts.premium + counts.free + counts.other)) * 360}deg, #10b981 ${counts.premium + counts.free + counts.other === 0 ? 0 : (counts.premium / (counts.premium + counts.free + counts.other)) * 360}deg ${counts.premium + counts.free + counts.other === 0 ? 0 : ((counts.premium + counts.free) / (counts.premium + counts.free + counts.other)) * 360}deg, #9ca3af ${counts.premium + counts.free + counts.other === 0 ? 0 : ((counts.premium + counts.free) / (counts.premium + counts.free + counts.other)) * 360}deg 360deg)`,
                    boxShadow: "0 10px 30px rgba(0,0,0,0.15)",
                    position: "relative",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Box
                    sx={{
                      position: "absolute",
                      width: 100,
                      height: 100,
                      borderRadius: "50%",
                      backgroundColor: "background.paper",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      boxShadow: "inset 0 0 0 1px rgba(0,0,0,0.06)",
                    }}
                  >
                    <Typography variant="subtitle1" fontWeight={700}>
                      {counts.premium + counts.free + counts.other}
                    </Typography>
                  </Box>
                </Box>
                <Stack spacing={0.5} sx={{ width: "100%" }}>
                  <LegendItem color="#4f46e5" label="Premium" value={counts.premium} />
                  <LegendItem color="#10b981" label="Free" value={counts.free} />
                  <LegendItem color="#9ca3af" label="Other" value={counts.other} />
                </Stack>
              </Paper>
            </Grid>
            <Grid item xs={12} md={8}>
              <Paper variant="outlined" sx={{ p: 0 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Name</TableCell>
                      <TableCell>Email</TableCell>
                        <TableCell>Role</TableCell>
                      <TableCell>Plan</TableCell>
                      <TableCell align="right">{translate("Tokens") || "Tokens"}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rows.map((r) => (
                      <TableRow key={r.id} hover>
                        <TableCell>{r.name}</TableCell>
                        <TableCell>{r.email}</TableCell>
                        <TableCell>{r.role}</TableCell>
                        <TableCell>
                          {r.plan === "free"
                            ? "Free"
                            : r.plan === "premium"
                            ? "Premium"
                            : r.plan === "none"
                            ? translate("PlanNoneLabel") || "No tenant"
                            : translate("PlanUnknownLabel") || "Unknown"}
                        </TableCell>
                        <TableCell align="right">
                          {r.tokens.toLocaleString()}
                        </TableCell>
                      </TableRow>
                    ))}
                    {!rows.length && (
                      <TableRow>
                        <TableCell colSpan={5} align="center">
                          {loadingProfiles
                            ? translate("LoadingUsers") || "Loading users..."
                            : translate("NoUsersFound") || "No users found"}
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </Paper>
            </Grid>
          </Grid>
        </Stack>
      </Box>
    </Box>
  );
}

function LegendItem({ color, label, value }) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
      <Box
        sx={{
          width: 12,
          height: 12,
          borderRadius: "3px",
          backgroundColor: color,
          boxShadow: "0 2px 6px rgba(0,0,0,0.2)",
        }}
      />
      <Typography variant="body2" sx={{ flex: 1 }}>
        {label}
      </Typography>
      <Typography variant="body2" fontWeight={700}>
        {value}
      </Typography>
    </Box>
  );
}
